/**
 * STEP 4 — Multi-Magasin RBAC banner.
 *
 * Displayed (sticky, below the main header) when the active magasin session
 * is a magasin where the current user only has VENDEUR-tier access (i.e.
 * `admin_magasin` interacting with a magasin outside their `assigned_magasins`).
 *
 * The banner explicitly tells the user their tier ("Mode commercial — Tu
 * agis sur <NAME> avec les droits d'un vendeur") so they don't get a
 * surprise 403 on stock/prix/compta actions, which the UI greys out
 * elsewhere.
 */
import React from 'react';
import { Briefcase } from 'lucide-react';

import { usePermissions } from '../contexts/PermissionsContext';

export default function CommercialModeBanner() {
  const {
    currentSessionMagasin,
    isCommercialMode,
    getMagasinName,
    getMagasinColor,
  } = usePermissions();

  if (!currentSessionMagasin) return null;
  if (!isCommercialMode(currentSessionMagasin)) return null;

  const name = getMagasinName(currentSessionMagasin) || currentSessionMagasin;
  const color = getMagasinColor(currentSessionMagasin) || '#3b82f6';

  return (
    <div
      data-testid="commercial-mode-banner"
      className="sticky top-16 z-40 w-full max-w-full min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 flex items-start sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm font-medium whitespace-normal break-words overflow-hidden"
      style={{
        background: `linear-gradient(90deg, ${color}22 0%, ${color}11 100%)`,
        borderBottom: `1px solid ${color}55`,
        color: '#dbeafe',
      }}
    >
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
        style={{ background: `${color}33`, color }}
      >
        <Briefcase className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0 leading-snug">
        <span className="font-bold" style={{ color }}>Mode commercial</span>
        <span className="mx-1.5 sm:mx-2 text-gray-300">·</span>
        <span className="text-gray-100">
          Vous agissez sur <span className="font-semibold">{name}</span> avec les droits d'un <span className="font-semibold">vendeur</span>.
        </span>
        <span className="hidden md:inline text-xs text-gray-300 md:ml-2">
          Encaissement, transferts et réparations autorisés. Modifications stock, prix et compta désactivées.
        </span>
      </div>
    </div>
  );
}
